import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  getGrouping as serverGetGrouping,
} from 'api/taxi.backend';
import {
  updateFormValue,
  setSupportsDegreeOffering,
  setAvailableCredentialTypes,
  setAvailablePrograms,
  setAvailableProgramsOfStudy,
  updateFormValidity,
} from 'containers/FormBuilder/actions';
import {
  selectDegreeOffering,
  selectProgramOfStudy,
} from 'containers/FormBuilder/selectors';
import makeSelectCredentialTypeList from 'containers/CredentialTypeList/selectors';
import {
  ERROR_TEXT_NO_DEGREE_OFFERING,
  ERROR_TEXT_NO_PROGRAM_OF_STUDY,
} from 'langs/en/errors';
import {
  programsContainDegreeOffering,
  getCredentialTypesFromProgramsOfStudy,
  getDataFromPrograms,
} from 'containers/FormBuilder/utils';

import { useFetchSfFieldData } from './useFetchSfFieldData';

/**
 * Custom hook to handle fetching grouping details.
 */
export const useFetchGroupingDetails = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { fetchFieldData } = useFetchSfFieldData();

  const selectCredentialTypeList = useMemo(makeSelectCredentialTypeList, []);
  const { credentialTypeList = [] } = useSelector(selectCredentialTypeList) || {};

  // pendingTaskRef stores data from the API while we wait for the credentialTypeList to populate
  const pendingTaskRef = useRef(null);

  /**
   * Handles logic to proceed once a valid credential type list is available
   */
  const continueProcessing = useCallback((renamedPrograms, programsOfStudy, currentCredentials) => {
    const supportsDegreeOffering = programsContainDegreeOffering(renamedPrograms);
    dispatch(setSupportsDegreeOffering(supportsDegreeOffering));

    const availableCredentialTypes = getCredentialTypesFromProgramsOfStudy(programsOfStudy, currentCredentials);
    dispatch(setAvailableCredentialTypes(availableCredentialTypes));

    dispatch(setAvailablePrograms(renamedPrograms));
    dispatch(setAvailableProgramsOfStudy(programsOfStudy));

    const state = store.getState();
    const currentDegreeOffering = selectDegreeOffering()(state);
    const currentProgramOfStudy = selectProgramOfStudy()(state);

    if (supportsDegreeOffering) {
      if (!currentDegreeOffering) {
        dispatch(updateFormValidity(['degreeOffering'], false, ERROR_TEXT_NO_DEGREE_OFFERING));
      }
    } else {
      const singleProgramData = getDataFromPrograms(renamedPrograms);
      dispatch(updateFormValue(['salesforceOrg'], singleProgramData.salesforceOrg, false));
      dispatch(updateFormValue(['programId'], singleProgramData.programId, false));
      fetchFieldData(singleProgramData.salesforceOrg);

      if (!currentProgramOfStudy) {
        dispatch(updateFormValidity(['programOfStudy'], false, ERROR_TEXT_NO_PROGRAM_OF_STUDY));
      }
    }

    pendingTaskRef.current = null;
  }, [dispatch, store]);

  /**
   * Main trigger function used by components
   */
  const fetchGroupingDetails = useCallback(async (groupingId) => {
    try {
      const response = await serverGetGrouping(groupingId, false);
      if (!response) return;

      const { id, name: groupingName, programs = [], programsOfStudy = [] } = response;

      dispatch(updateFormValue(['groupingId'], id, false));
      dispatch(updateFormValue(['groupingName'], groupingName, false));

      const renamedPrograms = programs.map((program) => ({
        programId: program.id,
        programFullName: program.fullName,
        programOffering: program.offering,
        degreeOffering: program.degreeOffering,
        salesforceOrg: program.salesforceOrg,
      }));

      const state = store.getState();
      const { credentialTypeList: latestCredentials = [] } = selectCredentialTypeList(state) || {};

      if (latestCredentials.length === 0) {
        pendingTaskRef.current = { renamedPrograms, programsOfStudy };
        return;
      }

      continueProcessing(renamedPrograms, programsOfStudy, latestCredentials);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Hook Error] fetchGroupingDetails failed:', e);
    }
  }, [dispatch, continueProcessing, store, selectCredentialTypeList]);

  /**
   * Acts as the "resume" trigger. When credentialTypeList populates,
   * it checks if a previous execution was paused (via pendingTaskRef).
   */
  useEffect(() => {
    if (credentialTypeList.length > 0 && pendingTaskRef.current) {
      const { renamedPrograms, programsOfStudy } = pendingTaskRef.current;
      continueProcessing(renamedPrograms, programsOfStudy, credentialTypeList);
    }
  }, [credentialTypeList, continueProcessing]);

  return { fetchGroupingDetails };
};
